import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, InlineEmptyStateOnboarding } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, ProcessTemplateParameterResource, ScopeValues, TemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { Permission, Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useMemo } from "react";
import type { ProcessTemplateProducerActionDispatcher } from "~/analytics/Analytics";
import { Action } from "~/analytics/Analytics";
import TemplateParameterEditorDialog from "~/areas/platformHub/processTemplates/TemplateParameterEditorDialog";
import { SortActionTemplateParametersButton } from "~/components/ActionTemplateParametersList/index";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { TemplateParametersTable } from "./TemplateParametersTable";
interface ProcessTemplateParametersTabProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
    dispatchProcessTemplateAction: ProcessTemplateProducerActionDispatcher | undefined;
    processTemplateLink: string | undefined;
}
export function ProcessTemplateParametersTab({ parameters, onChange, dispatchProcessTemplateAction, processTemplateLink }: ProcessTemplateParametersTabProps) {
    const { result: environments } = useQuery((repository) => repository.PlatformHub.getEnvironmentSummaries(), [], "Get environment summaries");
    const { result: platformHubAccounts } = useQuery((repository) => repository.PlatformHubAccounts.get({ Take: Repository.takeAll }), [], "Get all platform hub accounts");
    const availableScopeValues = useMemo(() => ({ ...defaultScopeValues, Environments: environments?.map((e) => ({ Id: `${e.SpaceId}/${e.Id}`, Name: `${e.Name} (${e.SpaceName})` })) ?? [] }), [environments]);
    const onAdd = (parameter: ProcessTemplateParameterResource) => {
        onChange([...parameters, parameter]);
        if (dispatchProcessTemplateAction) {
            dispatchProcessTemplateAction("Process Template Parameter Created", { type: "Process Template Parameter Created", action: Action.Add, controlType: parameter.DisplaySettings["Octopus.ControlType"] });
        }
    };
    if (parameters.length === 0) {
        return (<div className={styles.emptyStateContainer}>
                <TemplateParametersOnboarding onAdd={onAdd} parameters={parameters}/>
            </div>);
    }
    if (!platformHubAccounts || !environments) {
        return null;
    }
    return (<>
            <div className={styles.headerContainer}>
                <OpenReorderParametersButton parameters={parameters} onChange={onChange}/>
                <AddTemplateParameterButton parameters={parameters} onAdd={onAdd} type={ActionButtonType.Secondary}/>
            </div>
            <TemplateParametersTable parameters={parameters} onChange={onChange} availableScopeValues={availableScopeValues} platformHubAccounts={platformHubAccounts.Items} processTemplateLink={processTemplateLink}/>
        </>);
}
export const baseCellHeightInRem = 3;
export const multipleValuesAndScopesIndicatorHeightInRem = 1.75;
export const styles = {
    emptyStateContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
    }),
    headerContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
        gap: space[12],
        paddingRight: space[16],
        paddingTop: space[24],
        paddingBottom: space[24],
    }),
};
interface AddTemplateParameterButtonProps {
    parameters: TemplateParameterResource[];
    onAdd: (parameter: TemplateParameterResource) => void;
    type: ActionButtonType;
}
export function AddTemplateParameterButton({ parameters, onAdd, type }: AddTemplateParameterButtonProps) {
    return (<PermissionCheck key="AddParameterPermission" {...parameterEditPermission}>
            <OpenDialogButton key="AddParameter" label={`Add parameter`} type={type} renderDialog={({ open, closeDialog }) => (open ? <TemplateParameterEditorDialog allParameters={parameters} onOk={onAdd} open={open} close={closeDialog}/> : null)}/>
        </PermissionCheck>);
}
interface OpenReorderParametersButtonProps {
    parameters: TemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
}
export function OpenReorderParametersButton({ parameters, onChange }: OpenReorderParametersButtonProps) {
    return <SortActionTemplateParametersButton parameters={parameters} onChange={onChange} permission={parameterEditPermission} name={"parameter"}/>;
}
interface ParametersOnboardingProps {
    parameters: TemplateParameterResource[];
    onAdd: (parameter: TemplateParameterResource) => void;
}
export function TemplateParametersOnboarding({ onAdd, parameters }: ParametersOnboardingProps) {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const addParameterAction: PrimaryPageAction = {
        type: "button",
        label: "Add Parameter",
        onClick: () => setIsDialogOpen(true),
        hasPermissions: isAllowed(parameterEditPermission),
    };
    return (<>
            <TemplateParameterEditorDialog allParameters={parameters} onOk={onAdd} open={isDialogOpen} close={() => setIsDialogOpen(false)}/>
            <InlineEmptyStateOnboarding title="This template doesn’t have any parameters yet." description="Start creating by clicking on Add Parameter" illustration="NoEntities" actions={{ primary: addParameterAction }}/>
        </>);
}
const parameterEditPermission = {
    permission: Permission.PlatformHubEdit,
};
export const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
